import { useQueryClient } from '@tanstack/react-query';
import type { CabinetPageData } from '../types/cabinet-page';
import type { FieldConfig } from '../../../company-page/src/components/modules/GeneralModule';
import GeneralModule from '../../../company-page/src/components/modules/GeneralModule';
import ModuleCard from '../../../company-page/src/components/modules/ModuleCard';
import useEditMode from '../../../company-page/src/hooks/useEditMode';
import FeatureGateNotice from '@/hub-shared/components/FeatureGateNotice';
import { __ } from '@/hub-shared/utils/i18n';
import type { PortalGateDecision } from '../gates/portalGates';

export const CABINET_GENERAL_FIELDS: FieldConfig[] = [
  { key: 'name', label: 'Raison sociale' },
  { key: 'legal_form', label: 'Forme juridique' },
  { key: 'siret', label: 'SIRET' },
  // Derived from the SIRET's first nine digits; shown for reference, never edited directly.
  { key: 'siren', label: 'SIREN', readOnly: true },
  { key: 'fiscal_year_start', label: "Début d'exercice", inputType: 'date' },
  { key: 'fiscal_year_end', label: "Fin d'exercice", inputType: 'date' },
  { key: 'nace_codes', label: 'Code NACE' },
  { key: 'headquarters_address', label: 'Siège social' },
  { key: 'headquarters_postal_code', label: 'Code postal' },
  { key: 'headquarters_town', label: 'Ville' },
  { key: 'headquarters_country', label: 'Pays' },
];

const CABINET_CONTACT_FIELDS: FieldConfig[] = [
  { key: 'website', label: 'Site internet' },
  { key: 'phone', label: 'Téléphone' },
  { key: 'email', label: 'Email' },
];

type CabinetInfoTabProps = {
  data: CabinetPageData;
  queryKey: unknown[];
  bootstrapKey: string;
  cabinetProfileEditGate: PortalGateDecision;
};

const CabinetInfoTab = ({ data, queryKey, bootstrapKey, cabinetProfileEditGate }: CabinetInfoTabProps) => {
  const canEditCabinetProfile = cabinetProfileEditGate.allowed;
  const queryClient = useQueryClient();
  const { isEditing, localData, toggle, cancel, setField, save, isSaving, saveError } =
    useEditMode<CabinetPageData>(data, 'cabinet');

  const handleSave = () => {
    void save(bootstrapKey, (updatedData?: CabinetPageData) => {
      if (updatedData) {
        queryClient.setQueryData(queryKey, updatedData);
      }
      void queryClient.invalidateQueries({ queryKey });
    });
  };

  const isEditingAllowed = canEditCabinetProfile && isEditing;

  return (
    <div className="info-tab cabinet-info-tab">
      {!canEditCabinetProfile ? (
        <FeatureGateNotice decision={cabinetProfileEditGate} />
      ) : null}
      {saveError ? <p className="info-tab__error">{saveError}</p> : null}

      <ModuleCard title={__('Informations générales')}>
        <GeneralModule
          data={localData as unknown as Record<string, unknown>}
          isEditing={isEditingAllowed}
          onChange={setField}
          fields={CABINET_GENERAL_FIELDS}
        />
      </ModuleCard>

      <ModuleCard title={__('Informations de contact')}>
        <GeneralModule
          data={localData as unknown as Record<string, unknown>}
          isEditing={isEditingAllowed}
          onChange={setField}
          fields={CABINET_CONTACT_FIELDS}
        />
      </ModuleCard>

      {canEditCabinetProfile ? (
        <div className="info-tab__actions">
          {isEditing ? (
            <>
              <button
                type="button"
                className="btn-primary"
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? __('Enregistrement…') : __('Enregistrer')}
              </button>
              <button type="button" className="btn-secondary" onClick={cancel} disabled={isSaving}>
                {__('Annuler')}
              </button>
            </>
          ) : (
            <button type="button" className="btn-primary" onClick={toggle}>
              {__('Modifier les données')}
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default CabinetInfoTab;
